// Inputs for the Seguimiento position (design 1b "Sub-etapa map", Claude Design project
// "Multiproyectos · Backoffice Seguimiento"): pick the current sub-etapa from a chip map
// and set its %. Per-etapa progress is derived from that and shown read-only.
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { change, Field } from 'redux-form';
import { FormDataConsumer, REDUX_FORM_NAME } from 'react-admin';
import { deriveProgress } from '@shared/tracking-timeline';
import { parsePosition, positionKey } from '~utils/trackingPosition';
import styles from './styles.module.scss';

const formatPct = value => `${Math.round((Number(value) || 0) * 10) / 10} %`;

const showError = meta => meta && meta.error && (meta.touched || meta.submitFailed);

// One row per etapa; an etapa without sub-etapas is a single option of its own.
const positionRows = stages =>
  stages.map((stage, stageIndex) => ({
    stage,
    options: (stage.subStages && stage.subStages.length
      ? stage.subStages.map(subStage => ({ key: positionKey(stage.id, subStage.id), name: subStage.name }))
      : [{ key: positionKey(stage.id, null), name: stage.name }]
    ).map((option, optionIndex) => ({ ...option, rank: [stageIndex, optionIndex] }))
  }));

const findOption = (rows, key) =>
  rows.reduce((found, row) => found || row.options.find(option => option.key === key), null);

const isBefore = (a, b) => a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);

const chipClass = (option, current) => {
  if (current && option.key === current.key) return `${styles.chip} ${styles.chipCurrent}`;
  if (current && isBefore(option.rank, current.rank)) return `${styles.chip} ${styles.chipDone}`;
  return styles.chip;
};

const SubStageMap = ({ input, meta, rows }) => {
  const current = findOption(rows, input.value);
  return (
    <div className={styles.map} role="radiogroup" aria-label="Sub-etapa actual">
      {rows.map(row => (
        <div className={styles.mapRow} key={row.stage.id}>
          <span className={styles.mapStage}>{row.stage.name}</span>
          <div className={styles.chips}>
            {row.options.map(option => (
              <button
                key={option.key}
                type="button"
                role="radio"
                aria-checked={Boolean(current && option.key === current.key)}
                className={chipClass(option, current)}
                onClick={() => input.onChange(option.key)}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
      ))}
      {showError(meta) && <p className={styles.error}>{meta.error}</p>}
    </div>
  );
};

const ProgressInput = ({ input, meta, label }) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <div className={styles.pct}>
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        aria-label={label}
        value={input.value === '' ? 0 : input.value}
        onChange={event => input.onChange(Number(event.target.value))}
      />
      <input
        className={styles.pctNumber}
        type="number"
        min="0"
        max="100"
        step="1"
        aria-label={label}
        value={input.value}
        onChange={event => input.onChange(event.target.value === '' ? '' : Number(event.target.value))}
      />
    </div>
    {showError(meta) && <p className={styles.error}>{meta.error}</p>}
  </div>
);

/** The "position" panel: current sub-etapa (chip map) and its %. */
export const CurrentPositionInput = ({ record }) => {
  const rows = positionRows((record && record.stages) || []);
  return (
    <FormDataConsumer>
      {({ formData }) => {
        const current = findOption(rows, formData && formData.position);
        return (
          <div className={styles.positionPanel}>
            <div className={styles.field}>
              <span className={styles.label}>Sub-etapa actual</span>
              <Field name="position" component={SubStageMap} rows={rows} />
            </div>
            <Field
              name="currentProgress"
              component={ProgressInput}
              label={current ? `Avance de "${current.name}" (%)` : 'Avance de la sub-etapa (%)'}
            />
          </div>
        );
      }}
    </FormDataConsumer>
  );
};

// Keeps stages[i].progress (read-only in the grid, used by validation) equal to what the
// position implies, with the same rule as the API (shared deriveProgress).
class ProgressSync extends Component {
  componentDidMount() {
    this.sync();
  }

  componentDidUpdate() {
    this.sync();
  }

  sync() {
    const { formData, changeField } = this.props;
    const stages = formData.stages || [];
    const progress = deriveProgress(stages, { ...parsePosition(formData.position), currentProgress: formData.currentProgress });
    stages.forEach((stage, index) => {
      if (stage.progress !== progress[index]) {
        changeField(REDUX_FORM_NAME, `stages[${index}].progress`, progress[index]);
      }
    });
  }

  render() {
    return null;
  }
}

const ConnectedProgressSync = connect(null, { changeField: change })(ProgressSync);

export const DerivedProgressSync = () => (
  <FormDataConsumer>{({ formData }) => <ConnectedProgressSync formData={formData || {}} />}</FormDataConsumer>
);

const ProgressDisplay = ({ input, meta, label }) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <div className={styles.auto}>
      <span className={styles.mini}>
        <span style={{ width: `${Number(input.value) || 0}%` }} />
      </span>
      {formatPct(input.value)}
    </div>
    {showError(meta) && <p className={styles.error}>{meta.error}</p>}
  </div>
);

/** Read-only per-etapa progress cell for the etapa grid (inside SimpleFormIterator). */
export const AutoProgressField = ({ source, label }) => <Field name={source} component={ProgressDisplay} label={label} />;
